/**
 * ApplyKeywordDialog — Keyword-Vorschlag per 1-Klick in eine Google-Ads-
 * Anzeigengruppe uebernehmen (Kampagne -> Anzeigengruppe waehlen -> hinzufuegen).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplyKeywordDialog({ open, onClose, companyId, keyword }) {
  const [campaigns, setCampaigns] = useState(null);
  const [campaignId, setCampaignId] = useState("");
  const [adGroups, setAdGroups] = useState(null);
  const [adGroupId, setAdGroupId] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !companyId) return;
    setErr(""); setCampaigns(null); setCampaignId(""); setAdGroups(null); setAdGroupId("");
    axios.get(`${API}/google-ads/${companyId}/campaigns`, { withCredentials: true })
      .then((r) => setCampaigns(r.data.campaigns || []))
      .catch((e) => setErr(e.response?.data?.detail || "Kampagnen laden fehlgeschlagen"));
  }, [open, companyId]);

  useEffect(() => {
    if (!campaignId) { setAdGroups(null); setAdGroupId(""); return; }
    setAdGroups(null);
    axios.get(`${API}/google-ads/${companyId}/campaigns/${campaignId}/keywords`, { withCredentials: true })
      .then((r) => {
        const groups = r.data.ad_groups || [];
        setAdGroups(groups);
        if (groups.length === 1) setAdGroupId(groups[0].id);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Anzeigengruppen laden fehlgeschlagen"));
  }, [campaignId, companyId]);

  const apply = async () => {
    setBusy("apply");
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/adgroups/${adGroupId}/keywords`,
        { text: keyword.text, match_type: keyword.match_type }, { withCredentials: true });
      toast.success(r.data.message);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Uebernahme fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="apply-keyword-dialog">
        <DialogHeader><DialogTitle>Keyword in Google Ads übernehmen</DialogTitle></DialogHeader>
        {keyword && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">{keyword.text}</span>
              <Badge variant="outline" className="text-[10px]">{keyword.match_type}</Badge>
            </div>
            {err && <p className="text-xs text-red-600" data-testid="apply-kw-error">{err}</p>}
            {!err && !campaigns && <Loader2 className="w-4 h-4 animate-spin" />}
            {campaigns && (
              <div>
                <label className="text-xs font-medium">Kampagne</label>
                <Select value={campaignId} onValueChange={setCampaignId}>
                  <SelectTrigger className="mt-1" data-testid="apply-kw-campaign"><SelectValue placeholder="Kampagne wählen" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {campaignId && adGroups && (
              <div>
                <label className="text-xs font-medium">Anzeigengruppe</label>
                <Select value={adGroupId} onValueChange={setAdGroupId}>
                  <SelectTrigger className="mt-1" data-testid="apply-kw-adgroup"><SelectValue placeholder="Anzeigengruppe wählen" /></SelectTrigger>
                  <SelectContent>
                    {adGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={apply} disabled={!adGroupId || busy === "apply"} className="w-full" data-testid="apply-kw-submit">
              {busy === "apply" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Keyword hinzufügen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
